import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X, Volume2, User, Save, Cpu, Zap } from 'lucide-react';
import VoiceSettings from './VoiceSettings';
import { useAuthStore } from '../../stores';
import llmService from '../../services/llmService';
import { LLMModel, MCPFunction } from '../../types';
import { APP_VERSION, APP_BUILD_DATE } from '../../config/version';

interface UnifiedSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

type Tab = 'voice' | 'account' | 'models' | 'functions';

const UnifiedSettings: React.FC<UnifiedSettingsProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<Tab>('voice');
  const { user, updateSettings, isLoading } = useAuthStore();
  const [saveHistory, setSaveHistory] = useState(user?.save_history === 1);
  const [balance, setBalance] = useState(user?.balance?.toString() || '0');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [models, setModels] = useState<LLMModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [functions, setFunctions] = useState<MCPFunction[]>([]);
  const [functionsLoading, setFunctionsLoading] = useState(false);
  
  const isAdmin = user?.role === 'admin';
  
  // Логируем информацию о пользователе для отладки
  useEffect(() => {
    if (isOpen) {
      console.log(`👤 [UnifiedSettings] Пользователь: ${user?.username}, роль: ${user?.role}, isAdmin: ${isAdmin}`);
    }
  }, [isOpen, user, isAdmin]);

  // Загружаем модели при открытии вкладки "Модели"
  useEffect(() => {
    console.log(`🔍 [UnifiedSettings] Проверка загрузки моделей: isOpen=${isOpen}, activeTab=${activeTab}, isAdmin=${isAdmin}, models.length=${models.length}, modelsLoading=${modelsLoading}`);
    
    // Загружаем модели когда:
    // 1. Настройки открыты
    // 2. Активна вкладка "Модели"
    // 3. Пользователь админ
    // 4. Модели еще не загружены (не загружаем повторно если уже загружены)
    if (isOpen && activeTab === 'models' && isAdmin && models.length === 0 && !modelsLoading) {
      console.log('✅ [UnifiedSettings] Условия выполнены, запускаем загрузку моделей');
      loadModels();
    } else if (!isOpen) {
      // Когда настройки закрываются, сбрасываем состояние (опционально)
      // console.log('🔒 [UnifiedSettings] Настройки закрыты');
    } else if (activeTab !== 'models') {
      // console.log(`⏸️ [UnifiedSettings] Активна вкладка "${activeTab}", не "models"`);
    } else if (!isAdmin) {
      console.log('❌ [UnifiedSettings] Пользователь не является администратором');
    } else if (models.length > 0) {
      console.log(`✅ [UnifiedSettings] Модели уже загружены (${models.length} шт.)`);
    } else if (modelsLoading) {
      console.log('⏳ [UnifiedSettings] Модели уже загружаются...');
    }
  }, [isOpen, activeTab, isAdmin, models.length, modelsLoading]);

  // Загружаем функции при открытии вкладки "Функции"
  useEffect(() => {
    if (isOpen && activeTab === 'functions' && functions.length === 0 && !functionsLoading) {
      loadFunctions();
    }
  }, [isOpen, activeTab, functions.length, functionsLoading]);

  const loadModels = async () => {
    setModelsLoading(true);
    try {
      console.log('📥 [UnifiedSettings] Загрузка всех моделей для админа...');
      // Для админа загружаем все модели (включая отключенные)
      const loadedModels = await llmService.getAllModels();
      console.log(`✅ [UnifiedSettings] Загружено моделей: ${loadedModels.length}`);
      setModels(loadedModels);
    } catch (error: any) {
      console.error('❌ [UnifiedSettings] Ошибка загрузки моделей:', error);
      setMessage({ type: 'error', text: `Не удалось загрузить список моделей: ${error.message || 'Неизвестная ошибка'}` });
    } finally {
      setModelsLoading(false);
    }
  };

  const loadFunctions = async () => {
    setFunctionsLoading(true);
    try {
      console.log('📥 [UnifiedSettings] Загрузка функций...');
      // Для админа загружаем все функции (включая отключенные)
      const loadedFunctions = isAdmin 
        ? await llmService.getAllFunctions()
        : await llmService.getFunctions(false);
      console.log(`✅ [UnifiedSettings] Загружено функций: ${loadedFunctions.length}`);
      setFunctions(loadedFunctions);
    } catch (error: any) {
      console.error('❌ [UnifiedSettings] Ошибка загрузки функций:', error);
      setMessage({ type: 'error', text: `Не удалось загрузить список функций: ${error.message || 'Неизвестная ошибка'}` });
    } finally {
      setFunctionsLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    try {
      await updateSettings({
        save_history: saveHistory ? 1 : 0,
        balance: user?.role === 'admin' ? parseFloat(balance) : undefined,
      });
      setMessage({ type: 'success', text: 'Настройки успешно сохранены' });
      
      // Обновляем локальное состояние из обновленного user
      if (user) {
        setSaveHistory(user.save_history === 1);
        setBalance((user.balance ?? 0).toString());
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Ошибка сохранения настроек' });
    }
  };

  return (
    <>
      <Overlay onClick={onClose} />
      <Modal>
        <Header>
          <Title>⚙️ Настройки</Title>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </Header>

        <Tabs>
          <Tab 
            $active={activeTab === 'voice'} 
            onClick={() => setActiveTab('voice')}
          >
            <Volume2 size={18} />
            <span>Голос</span>
          </Tab>
          <Tab 
            $active={activeTab === 'account'} 
            onClick={() => setActiveTab('account')}
          >
            <User size={18} />
            <span>Учётная запись</span>
          </Tab>
          {isAdmin && (
            <>
              <Tab 
                $active={activeTab === 'models'} 
                onClick={() => setActiveTab('models')}
              >
                <Cpu size={18} />
                <span>Модели</span>
              </Tab>
              <Tab 
                $active={activeTab === 'functions'} 
                onClick={() => setActiveTab('functions')}
              >
                <Zap size={18} />
                <span>Функции</span>
              </Tab>
            </>
          )}
        </Tabs>

        <Content>
          {activeTab === 'voice' && (
            <>
              <VoiceSettings isInline />
              <VersionInfo>
                <VersionLabel>Версия приложения:</VersionLabel>
                <VersionValue>v{APP_VERSION}</VersionValue>
                <VersionDate>(сборка {APP_BUILD_DATE})</VersionDate>
              </VersionInfo>
            </>
          )}

          {activeTab === 'account' && user && (
            <>
              <AccountForm onSubmit={handleAccountSubmit}>
              {/* Баланс */}
              <Section>
                <SectionTitle>💰 Баланс счёта</SectionTitle>
                <BalanceDisplay $positive={(user.balance ?? 0) > 0}>
                  <BalanceAmount>{(user.balance ?? 0).toFixed(2)} ₽</BalanceAmount>
                  <BalanceLabel>Текущий баланс</BalanceLabel>
                </BalanceDisplay>
                
                {user.role === 'admin' && (
                  <FormGroup>
                    <Label htmlFor="balance">Изменить баланс</Label>
                    <Input
                      id="balance"
                      type="number"
                      step="0.01"
                      value={balance}
                      onChange={(e) => setBalance(e.target.value)}
                      placeholder="Введите сумму"
                    />
                    <Helper>Доступно только администраторам</Helper>
                  </FormGroup>
                )}
              </Section>

              {/* Сохранение истории */}
              <Section>
                <SectionTitle>📚 История запросов</SectionTitle>
                
                <CheckboxGroup>
                  <Checkbox
                    type="checkbox"
                    id="save_history"
                    checked={saveHistory}
                    onChange={(e) => setSaveHistory(e.target.checked)}
                  />
                  <CheckboxLabel htmlFor="save_history">
                    <strong>Сохранять историю запросов в базу данных</strong>
                    <CheckboxDescription>
                      При включении ваши чаты и сообщения будут сохраняться в базе данных. 
                      При выключении история будет доступна только до закрытия браузера.
                    </CheckboxDescription>
                  </CheckboxLabel>
                </CheckboxGroup>
              </Section>

              {/* Сообщения */}
              {message && (
                <Message type={message.type}>
                  {message.type === 'success' ? '✅' : '❌'} {message.text}
                </Message>
              )}

              {/* Кнопка сохранения */}
              <SubmitButton type="submit" disabled={isLoading}>
                <Save size={18} />
                {isLoading ? 'Сохранение...' : 'Сохранить изменения'}
              </SubmitButton>
            </AccountForm>
            <VersionInfo>
              <VersionLabel>Версия приложения:</VersionLabel>
              <VersionValue>v{APP_VERSION}</VersionValue>
              <VersionDate>(сборка {APP_BUILD_DATE})</VersionDate>
            </VersionInfo>
          </>
          )}

          {activeTab === 'models' && isAdmin && (
            <>
              <ModelsSection>
                <SectionTitle>🤖 Управление моделями</SectionTitle>
              {modelsLoading ? (
                <LoadingMessage>Загрузка моделей...</LoadingMessage>
              ) : models.length === 0 ? (
                <EmptyMessage>Модели не найдены</EmptyMessage>
              ) : (
                <ModelsTableContainer>
                  <ModelsTable>
                    <TableHeader>
                      <TableHeaderCell>Провайдер</TableHeaderCell>
                      <TableHeaderCell>Название</TableHeaderCell>
                      <TableHeaderCell>Цена за токен</TableHeaderCell>
                      <TableHeaderCell>Макс. токенов</TableHeaderCell>
                      <TableHeaderCell>Статус</TableHeaderCell>
                    </TableHeader>
                    <TableBody>
                      {models.map((model) => (
                        <TableRow key={model.id}>
                          <TableCell>{model.providerDisplayName || model.provider}</TableCell>
                          <TableCell>
                            <ModelName>{model.name}</ModelName>
                            {model.description && (
                              <ModelDescription>{model.description}</ModelDescription>
                            )}
                          </TableCell>
                          <TableCell>
                            {(model.costPerToken * 1000000).toFixed(2)} ₽/1M
                          </TableCell>
                          <TableCell>
                            {model.maxTokenAnswer.toLocaleString('ru-RU')}
                          </TableCell>
                          <TableCell>
                            <StatusBadge $enabled={model.enabled !== false} $available={model.isAvailable}>
                              {model.enabled !== false ? '✅ Включена' : '❌ Отключена'}
                              {!model.isAvailable && ' (недоступна)'}
                            </StatusBadge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </ModelsTable>
                </ModelsTableContainer>
              )}
              </ModelsSection>
              <VersionInfo>
                <VersionLabel>Версия приложения:</VersionLabel>
                <VersionValue>v{APP_VERSION}</VersionValue>
                <VersionDate>(сборка {APP_BUILD_DATE})</VersionDate>
              </VersionInfo>
            </>
          )}

          {activeTab === 'functions' && (
            <>
              <FunctionsSection>
                <SectionTitle>⚡ Управление функциями</SectionTitle>
              {functionsLoading ? (
                <LoadingMessage>Загрузка функций...</LoadingMessage>
              ) : functions.length === 0 ? (
                <EmptyMessage>Функции не найдены</EmptyMessage>
              ) : (
                <FunctionsTableContainer>
                  <FunctionsTable>
                    <FunctionsTableHeader>
                      <TableHeaderCell>Название</TableHeaderCell>
                      <TableHeaderCell>Описание</TableHeaderCell>
                      <TableHeaderCell>Параметры</TableHeaderCell>
                      <TableHeaderCell>Роли</TableHeaderCell>
                      <TableHeaderCell>Статус</TableHeaderCell>
                    </FunctionsTableHeader>
                    <TableBody>
                      {functions.map((func) => (
                        <FunctionsTableRow key={func.name}>
                          <TableCell>
                            <FunctionName>{func.name}</FunctionName>
                          </TableCell>
                          <TableCell>
                            <FunctionDescription>{func.description}</FunctionDescription>
                          </TableCell>
                          <TableCell>
                            {func.parameters.length > 0 ? (
                              <ParametersList>
                                {func.parameters.map((param, idx) => (
                                  <ParameterItem key={idx}>
                                    <ParameterName>{param.name}</ParameterName>
                                    <ParameterType>({param.type})</ParameterType>
                                    {param.required && <RequiredBadge>*</RequiredBadge>}
                                  </ParameterItem>
                                ))}
                              </ParametersList>
                            ) : (
                              <NoParameters>Нет параметров</NoParameters>
                            )}
                          </TableCell>
                          <TableCell>
                            <RolesList>
                              {func.requiredRoles.map((role, idx) => (
                                <RoleBadge key={idx}>{role}</RoleBadge>
                              ))}
                            </RolesList>
                          </TableCell>
                          <TableCell>
                            <StatusBadge $enabled={func.enabled} $available={true}>
                              {func.enabled ? '✅ Включена' : '❌ Отключена'}
                            </StatusBadge>
                            {func.maxCallsPerMinute && (
                              <RateLimitInfo>
                                Лимит: {func.maxCallsPerMinute}/мин
                              </RateLimitInfo>
                            )}
                          </TableCell>
                        </FunctionsTableRow>
                      ))}
                    </TableBody>
                  </FunctionsTable>
                </FunctionsTableContainer>
              )}
              </FunctionsSection>
              <VersionInfo>
                <VersionLabel>Версия приложения:</VersionLabel>
                <VersionValue>v{APP_VERSION}</VersionValue>
                <VersionDate>(сборка {APP_BUILD_DATE})</VersionDate>
              </VersionInfo>
            </>
          )}
        </Content>
      </Modal>
    </>
  );
};

export default UnifiedSettings;

// Styled Components

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 1000;
`;

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
  z-index: 1001;
  width: 95%;
  max-width: 700px;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 640px) {
    width: 100%;
    max-height: 100vh;
    border-radius: 0;
    top: 0;
    left: 0;
    transform: none;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  
  @media (max-width: 640px) {
    padding: 1rem;
  }
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: #f3f4f6;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  color: #6b7280;

  &:hover {
    background: #e5e7eb;
    color: #111827;
  }
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  padding: 0 1.5rem;
  flex-shrink: 0;
  overflow-x: auto;
  
  @media (max-width: 640px) {
    padding: 0 1rem;
  }
  
  /* Скрыть скроллбар, но оставить прокрутку */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const Tab = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border: none;
  background: none;
  color: ${props => props.$active ? '#0284c7' : '#6b7280'};
  font-weight: ${props => props.$active ? '600' : '500'};
  font-size: 0.875rem;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => props.$active ? '#0284c7' : 'transparent'};
    transition: all 0.2s;
  }

  &:hover {
    color: #0284c7;
  }

  span {
    @media (max-width: 500px) {
      display: none;
    }
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem 1.5rem;
  
  @media (max-width: 640px) {
    padding: 1rem;
  }
  
  /* Стилизация скроллбара */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    
    &:hover {
      background: #94a3b8;
    }
  }
`;

const AccountForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Section = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
`;

const BalanceDisplay = styled.div<{ $positive: boolean }>`
  background: ${props => props.$positive 
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
  };
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  margin-bottom: 1rem;
`;

const BalanceAmount = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.25rem;
`;

const BalanceLabel = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #0284c7;
    box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.1);
  }
`;

const Helper = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
`;

const CheckboxGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin-top: 0.25rem;
`;

const CheckboxLabel = styled.label`
  flex: 1;
  cursor: pointer;
  
  strong {
    display: block;
    font-size: 1rem;
    color: #111827;
    margin-bottom: 0.25rem;
  }
`;

const CheckboxDescription = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

const Message = styled.div<{ type: 'success' | 'error' }>`
  padding: 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  background: ${props => props.type === 'success' ? '#d1fae5' : '#fee2e2'};
  color: ${props => props.type === 'success' ? '#065f46' : '#991b1b'};
  border: 1px solid ${props => props.type === 'success' ? '#86efac' : '#fecaca'};
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(2, 132, 199, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ModelsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
`;

const EmptyMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
`;

const ModelsTableContainer = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  
  /* Стилизация скроллбара */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    
    &:hover {
      background: #94a3b8;
    }
  }
`;

const ModelsTable = styled.div`
  background: white;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 2fr 1fr 1fr 1.2fr;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
  font-weight: 600;
  font-size: 0.875rem;
  color: #374151;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableHeaderCell = styled.div`
  display: flex;
  align-items: center;
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 2fr 1fr 1fr 1.2fr;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #111827;
`;

const ModelName = styled.div`
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.25rem;
`;

const ModelDescription = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;
`;

const StatusBadge = styled.div<{ $enabled: boolean; $available: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => 
    !props.$available ? '#fee2e2' :
    props.$enabled ? '#d1fae5' : '#fef3c7'
  };
  color: ${props => 
    !props.$available ? '#991b1b' :
    props.$enabled ? '#065f46' : '#92400e'
  };
  border: 1px solid ${props => 
    !props.$available ? '#fecaca' :
    props.$enabled ? '#86efac' : '#fde68a'
  };
`;

// Styled Components для функций
const FunctionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FunctionsTableContainer = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  
  /* Стилизация скроллбара */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    
    &:hover {
      background: #94a3b8;
    }
  }
`;

const FunctionsTable = styled.div`
  background: white;
`;

const FunctionsTableHeader = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 2fr 2fr 1fr 1.2fr;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
  font-weight: 600;
  font-size: 0.875rem;
  color: #374151;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const FunctionsTableRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 2fr 2fr 1fr 1.2fr;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const FunctionName = styled.div`
  font-weight: 600;
  color: #111827;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
`;

const FunctionDescription = styled.div`
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.5;
`;

const ParametersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ParameterItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
`;

const ParameterName = styled.span`
  font-weight: 500;
  color: #111827;
  font-family: 'Courier New', monospace;
`;

const ParameterType = styled.span`
  color: #6b7280;
  font-style: italic;
`;

const RequiredBadge = styled.span`
  color: #dc2626;
  font-weight: 700;
  font-size: 0.875rem;
`;

const NoParameters = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  font-style: italic;
`;

const RolesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: #e0e7ff;
  color: #4338ca;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const RateLimitInfo = styled.div`
  font-size: 0.7rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;

const VersionInfo = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
  
  @media (max-width: 640px) {
    margin-top: 1.5rem;
    padding-top: 1rem;
  }
`;

const VersionLabel = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
`;

const VersionValue = styled.span`
  font-size: 1rem;
  color: #111827;
  font-weight: 600;
  font-family: 'Courier New', monospace;
`;

const VersionDate = styled.span`
  font-size: 0.7rem;
  color: #9ca3af;
  font-style: italic;
`;
